import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RotateCcw, Filter, AlertCircle, Loader2, X,
  ChevronDown, ChevronRight, Eye, EyeOff, Flag, ListTodo,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

const KIND_OPTIONS = [
  { value: "", label: "Alle Typen" },
  { value: "standort", label: "Standortlead" },
  { value: "gruender", label: "Gründerlead" },
  { value: "kontakt", label: "Kontaktanfrage" },
  { value: "dev_program", label: "Dev-Programm" },
  { value: "sales_partner", label: "Sales Partner" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "Alle Quellen" },
  { value: "website", label: "Website" },
  { value: "manual", label: "Manuell" },
  { value: "phone", label: "Telefon" },
  { value: "trade_fair", label: "Messe" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "email", label: "E-Mail" },
  { value: "referral", label: "Empfehlung" },
  { value: "sales_partner", label: "Sales Partner" },
  { value: "other", label: "Sonstige" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "Alle Prioritäten" },
  { value: "low", label: "Niedrig" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "Hoch" },
  { value: "urgent", label: "Dringend" },
];

const PRIORITY_TONE = {
  low: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
  normal: "border-white/10 bg-white/[0.04] text-zinc-400",
  high: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  urgent: "border-red-500/40 bg-red-500/10 text-red-300",
};

function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return iso; }
}

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function stageLabelFor(data, stageValue) {
  return data?.stages?.find((s) => s.stage === stageValue)?.label || stageValue;
}

export default function KanbanPage() {
  const [kind, setKind] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [priority, setPriority] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const search = useDebounced(searchInput, 350);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pendingDrop, setPendingDrop] = useState(null);
  const [dropNote, setDropNote] = useState("");
  const [savingDrop, setSavingDrop] = useState(false);

  const [dragLeadId, setDragLeadId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (kind) params.set("kind", kind);
      if (sourceType) params.set("source_type", sourceType);
      if (priority) params.set("priority", priority);
      if (search) params.set("search", search);
      if (includeArchived) params.set("include_archived", "true");
      const r = await axios.get(`${API}/admin/crm/kanban?${params}`, {
        headers: authHeaders(), timeout: 25000,
      });
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unbekannter Fehler");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [kind, sourceType, priority, search, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const handleDragStart = useCallback((leadId) => setDragLeadId(leadId), []);
  const handleDragEnd = useCallback(() => {
    setDragLeadId(null);
    setDragOverStage(null);
  }, []);

  const handleDrop = useCallback((toStage) => {
    if (!dragLeadId || !data) return;
    let fromStage = null;
    let lead = null;
    for (const s of data.stages) {
      const found = s.items.find((it) => it.id === dragLeadId);
      if (found) { fromStage = s.stage; lead = found; break; }
    }
    setDragLeadId(null);
    setDragOverStage(null);
    if (!lead || fromStage === toStage) return;
    setPendingDrop({ lead, fromStage, toStage });
    setDropNote("");
  }, [dragLeadId, data]);

  const confirmDrop = useCallback(async () => {
    if (!pendingDrop) return;
    setSavingDrop(true);
    try {
      await axios.patch(
        `${API}/admin/crm/leads/${pendingDrop.lead.id}/stage`,
        { pipeline_stage: pendingDrop.toStage, note: dropNote || undefined },
        { headers: authHeaders(), timeout: 20000 },
      );
      toast.success(`Stage geändert: ${pendingDrop.lead.pipeline_stage_label} → ${stageLabelFor(data, pendingDrop.toStage)}`);
      setPendingDrop(null);
      setDropNote("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail || err?.message;
      toast.error(msg || "Stage-Wechsel fehlgeschlagen");
    } finally {
      setSavingDrop(false);
    }
  }, [pendingDrop, dropNote, load, data]);

  const cancelDrop = () => { setPendingDrop(null); setDropNote(""); };

  const moveByDropdown = useCallback((lead, toStage) => {
    if (!lead || !toStage || toStage === lead.pipeline_stage) return;
    setPendingDrop({ lead, fromStage: lead.pipeline_stage, toStage });
    setDropNote("");
  }, []);

  const filtersActive = useMemo(
    () => Boolean(kind || sourceType || priority || search),
    [kind, sourceType, priority, search],
  );

  return (
    <div data-testid="journey-kanban-page" className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-zinc-100">Kanban</h1>
        <p className="text-sm text-zinc-400">
          Steuern Sie Leads nach Pipeline-Stage und behalten Sie offene nächste Schritte im Blick.
        </p>
      </header>

      <section className="space-y-3 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Suche nach Name, Firma, E-Mail, Region…"
              data-testid="journey-kanban-search"
              className="w-full rounded border border-white/10 bg-[#0B0F10] py-2 pl-9 pr-3 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40"
            />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="journey-kanban-refresh"
            className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 transition hover:border-white/20 hover:bg-white/5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Aktualisieren
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FilterSelect testId="journey-kanban-filter-kind" value={kind} onChange={setKind} options={KIND_OPTIONS} />
          <FilterSelect testId="journey-kanban-filter-source" value={sourceType} onChange={setSourceType} options={SOURCE_OPTIONS} />
          <FilterSelect testId="journey-kanban-filter-priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
          <button
            type="button"
            onClick={() => setIncludeArchived((v) => !v)}
            data-testid="journey-kanban-toggle-archived"
            className={[
              "inline-flex items-center justify-center gap-1.5 rounded border px-3 py-2 text-xs transition",
              includeArchived
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-white/10 text-zinc-400 hover:border-white/20 hover:bg-white/5",
            ].join(" ")}
          >
            {includeArchived ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Archivierte / abgeschlossene anzeigen
          </button>
        </div>

        {filtersActive ? (
          <button
            type="button"
            data-testid="journey-kanban-clear-all"
            onClick={() => { setKind(""); setSourceType(""); setPriority(""); setSearchInput(""); }}
            className="inline-flex items-center gap-1 text-[11px] text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
          >
            <X className="h-3 w-3" />
            Alle Filter zurücksetzen
          </button>
        ) : null}
      </section>

      {error ? (
        <div data-testid="journey-kanban-error" className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-5 text-sm text-red-200">
          <div className="mb-2 flex items-center gap-2 font-medium">
            <AlertCircle className="h-4 w-4" />
            Fehler beim Laden des Kanban
          </div>
          <div className="mb-3 text-xs text-red-200/80">{String(error)}</div>
          <button onClick={load} className="rounded border border-red-400/30 px-3 py-1.5 text-xs text-red-100 hover:bg-red-500/10">
            Erneut versuchen
          </button>
        </div>
      ) : loading ? (
        <div data-testid="journey-kanban-loading" className="space-y-2 rounded-md border border-white/[0.06] p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded bg-white/[0.03]" />
          ))}
        </div>
      ) : data ? (
        <>
          <div data-testid="journey-kanban-board" className="hidden gap-3 overflow-x-auto pb-2 md:flex">
            {data.stages.map((s) => (
              <KanbanColumn
                key={s.stage}
                stage={s}
                isDragOver={dragOverStage === s.stage}
                onDragOver={(e) => { e.preventDefault(); setDragOverStage(s.stage); }}
                onDragLeave={() => setDragOverStage((curr) => (curr === s.stage ? null : curr))}
                onDrop={() => handleDrop(s.stage)}
                renderCard={(item) => (
                  <KanbanCard
                    key={item.id}
                    item={item}
                    onDragStart={() => handleDragStart(item.id)}
                    onDragEnd={handleDragEnd}
                    isDragging={dragLeadId === item.id}
                  />
                )}
              />
            ))}
          </div>

          <div data-testid="journey-kanban-mobile" className="space-y-2 md:hidden">
            {data.stages.map((s) => (
              <MobileStage key={s.stage} stage={s} allStages={data.stages} onMove={moveByDropdown} />
            ))}
          </div>

          <div className="text-right text-xs text-zinc-500">
            {data.total} Karten · {data.stages.length} Stages
          </div>
        </>
      ) : null}

      {pendingDrop ? (
        <StageChangeModal
          drop={pendingDrop}
          allStages={data?.stages || []}
          note={dropNote}
          onNoteChange={setDropNote}
          saving={savingDrop}
          onConfirm={confirmDrop}
          onCancel={cancelDrop}
        />
      ) : null}
    </div>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Filter className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full rounded border border-white/10 bg-[#0B0F10] px-2 py-2 text-xs text-zinc-200 outline-none transition focus:border-emerald-500/40"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function KanbanColumn({ stage, isDragOver, onDragOver, onDragLeave, onDrop, renderCard }) {
  return (
    <div
      data-testid={`journey-kanban-column-${stage.stage}`}
      data-stage={stage.stage}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={[
        "flex w-72 shrink-0 flex-col rounded-md border bg-white/[0.01] transition",
        isDragOver ? "border-emerald-500/50 bg-emerald-500/[0.04]" : "border-white/[0.06]",
      ].join(" ")}
    >
      <header className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-300">{stage.label}</div>
        <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">
          {stage.count}
        </span>
      </header>
      <div className="flex max-h-[70vh] flex-1 flex-col gap-2 overflow-y-auto p-2">
        {stage.items.length === 0 ? (
          <div className="rounded border border-dashed border-white/[0.06] p-4 text-center text-[11px] text-zinc-600">
            Keine Karten
          </div>
        ) : (
          stage.items.map((it) => renderCard(it))
        )}
      </div>
    </div>
  );
}

function KanbanCard({ item, onDragStart, onDragEnd, isDragging }) {
  return (
    <div
      data-testid={`journey-kanban-card-${item.id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", item.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={[
        "group cursor-grab rounded border bg-[#0B0F10] p-3 transition active:cursor-grabbing",
        isDragging ? "border-emerald-500/50 opacity-60" : "border-white/[0.06] hover:border-emerald-500/30",
      ].join(" ")}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-zinc-100">
            {item.full_name || item.email || "—"}
          </div>
          {item.company ? <div className="truncate text-[11px] text-zinc-500">{item.company}</div> : null}
        </div>
        <span className="shrink-0 text-[10px] text-zinc-600">{formatDate(item.created_at)}</span>
      </div>

      <div className="mb-2 flex flex-wrap gap-1">
        <span className="inline-flex items-center rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">
          {item.kind_label}
        </span>
        <span className="inline-flex items-center rounded border border-white/5 px-1.5 py-0.5 text-[10px] text-zinc-500">
          {item.source_type_label}
        </span>
        {item.priority && item.priority !== "normal" ? (
          <span className={`inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] ${PRIORITY_TONE[item.priority] || PRIORITY_TONE.normal}`}>
            <Flag className="h-2.5 w-2.5" />
            {item.priority}
          </span>
        ) : null}
      </div>

      {item.next_step ? (
        <div className="mb-1 line-clamp-2 text-[11px] text-emerald-200/80">→ {item.next_step}</div>
      ) : null}
      {item.next_follow_up_at ? (
        <div className="text-[10px] text-zinc-500">Follow-up: {formatDate(item.next_follow_up_at)}</div>
      ) : null}

      {item.open_tasks_count > 0 ? (
        <div className={[
          "mt-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]",
          item.has_overdue_tasks
            ? "border border-red-500/30 bg-red-500/10 text-red-300"
            : "border border-white/10 bg-white/[0.04] text-zinc-400",
        ].join(" ")}>
          <ListTodo className="h-2.5 w-2.5" />
          {item.open_tasks_count} Task{item.open_tasks_count === 1 ? "" : "s"}
          {item.has_overdue_tasks ? ` · ${item.overdue_tasks_count} überfällig` : ""}
        </div>
      ) : null}

      <div className="mt-3 flex justify-end">
        <Link
          to={`/admin/journey/leads/${item.id}`}
          data-testid={`journey-kanban-open-${item.id}`}
          className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-0.5 text-[10px] text-zinc-400 transition hover:border-emerald-500/30 hover:text-emerald-300"
        >
          Öffnen
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function MobileStage({ stage, allStages, onMove }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`journey-kanban-mobile-stage-${stage.stage}`}
      className="rounded-md border border-white/[0.06] bg-white/[0.01]"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2"
      >
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-300">{stage.label}</span>
        <span className="flex items-center gap-2">
          <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">{stage.count}</span>
          <ChevronDown className={`h-3.5 w-3.5 text-zinc-500 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-white/[0.06] p-2">
          {stage.items.length === 0 ? (
            <div className="rounded border border-dashed border-white/[0.06] p-3 text-center text-[11px] text-zinc-600">
              Keine Karten
            </div>
          ) : (
            stage.items.map((it) => <MobileCard key={it.id} item={it} allStages={allStages} onMove={onMove} />)
          )}
        </div>
      ) : null}
    </div>
  );
}

function MobileCard({ item, allStages, onMove }) {
  return (
    <div
      data-testid={`journey-kanban-mobile-card-${item.id}`}
      className="rounded border border-white/[0.06] bg-[#0B0F10] p-3"
    >
      <div className="mb-1 text-sm font-medium text-zinc-100">{item.full_name || item.email || "—"}</div>
      {item.company ? <div className="mb-1 text-[11px] text-zinc-500">{item.company}</div> : null}
      <div className="mb-2 flex flex-wrap gap-1">
        <span className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-zinc-300">{item.kind_label}</span>
        <span className="rounded border border-white/5 px-1.5 py-0.5 text-[10px] text-zinc-500">{item.source_type_label}</span>
      </div>
      {item.next_step ? <div className="mb-2 text-[11px] text-emerald-200/80">→ {item.next_step}</div> : null}
      <div className="flex items-center justify-between gap-2">
        <select
          data-testid={`journey-kanban-mobile-move-${item.id}`}
          value={item.pipeline_stage || ""}
          onChange={(e) => onMove(item, e.target.value)}
          className="flex-1 rounded border border-white/10 bg-[#050607] px-2 py-1 text-[11px] text-zinc-200"
        >
          {allStages.map((s) => (
            <option key={s.stage} value={s.stage}>{s.label}</option>
          ))}
        </select>
        <Link
          to={`/admin/journey/leads/${item.id}`}
          className="rounded border border-white/10 px-2 py-1 text-[11px] text-zinc-300 hover:border-emerald-500/30 hover:text-emerald-300"
        >
          Öffnen
        </Link>
      </div>
    </div>
  );
}

function StageChangeModal({ drop, allStages, note, onNoteChange, saving, onConfirm, onCancel }) {
  const fromLabel = allStages.find((s) => s.stage === drop.fromStage)?.label || drop.fromStage || "—";
  const toLabel = allStages.find((s) => s.stage === drop.toStage)?.label || drop.toStage;
  return (
    <div
      data-testid="journey-kanban-drop-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-4 rounded-md border border-white/[0.08] bg-[#0B0F10] p-5"
      >
        <header className="space-y-1">
          <h2 className="text-sm font-semibold text-zinc-100">Stage ändern</h2>
          <p className="text-xs text-zinc-400">
            <span className="text-zinc-200">{drop.lead.full_name || drop.lead.email || "—"}</span> verschieben:
            <span className="ml-1 text-zinc-300">{fromLabel}</span>
            <span className="mx-1 text-zinc-600">→</span>
            <span className="text-emerald-300">{toLabel}</span>
          </p>
        </header>
        <label className="block space-y-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Notiz (optional)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            data-testid="journey-kanban-drop-note"
            placeholder="z.B. Erstgespräch sehr stark"
            className="w-full rounded border border-white/10 bg-[#050607] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            data-testid="journey-kanban-drop-cancel"
            className="rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-white/20 hover:bg-white/5"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onConfirm}
            data-testid="journey-kanban-drop-confirm"
            className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Stage ändern
          </button>
        </div>
      </div>
    </div>
  );
}
